import { useCallback, useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useParams } from "react-router-dom";
import { Collapse } from "reactstrap";

import AppTable from "../../AppTable/AppTable";
import SharedSelectedRecordsActions from "./SharedToolbar/SharedSelectedRecordsActions";
import UpdateRecordsDrawer from "../../Drawers/Records/UpdateRecordsDrawer";
import useToggle from "../../../hooks/useToggle";
import BulkApplyTagsDrawer from "../../Drawers/Records/BulkApplyTagsDrawer";
import useBulkDeleteRecords from "../../../api/api-hooks/records/useBulkDeleteRecords";
import StrUtils from "../../../utils/StrUtils";
import usePage from "../../../api/api-hooks/pages/usePage";
import CreateRecordDrawer from "../../Drawers/Records/CreateRecordDrawer";
import { Prefill } from "../../../types/GlobalInterfaces";
import MergeRecordsDrawer from "../../Drawers/Records/MergeRecordsDrawer/MergeRecordsDrawer";
import useModule from "../../../hooks/useModule";
import DataExportDrawer from "../../Drawers/DataExportDrawer";
import SkeletonLoader from "../../../elements/SkeletonLoader";
import { Module } from "../../../types/TenantInterfaces";
import AppEmptyState from "../../../elements/AppEmptyState";
import BulkDataTransforms from "../../Drawers/BulkDataTransformsDrawer";
import AddToListDrawer from "../../Drawers/AddToListDrawer";
import IconButton from "../../../elements/IconButton";

const emptyPrefill: Prefill[] = [];

/**
 * Function to get the title of a group based on provided metadata and module information.
 *
 * @param groupMetaData - Metadata containing information about the group.
 * It could be a string, array, or an object with a "_RecordName" property.
 * @param group - The API name of the field being grouped by.
 * This is used to find fallback field information from the module's fields if groupMetaData is not available.
 * @param mod - Optional module information to look up field details such as field name.
 * This is used to provide a more user-friendly name if groupMetaData is not available.
 */
export function GetGroupTitle(
  groupMetaData: any,
  group: string,
  mod?: Module | null
): string {
  if (groupMetaData) {
    if (typeof groupMetaData === "string") {
      // SingleSelect, Text
      return groupMetaData;
    } else if (Array.isArray(groupMetaData)) {
      // MutliSelect is an array of strings
      if (!groupMetaData.length) return "No Group";

      return groupMetaData.join(", ");
    } else {
      // SingleReference FieldType
      return groupMetaData._RecordName;
    }
  } else {
    const field = mod?.Fields?.find?.((f) => f?.APIName === group);
    // Location Field support
    const subField = group?.split(".")[2];

    return `No ${
      subField
        ? subField === "StateProvince"
          ? "State"
          : subField
        : field?.Name || group
    }`;
  }
}

type GroupData = {
  data: any[];
  title: string;
  id: string;
};

const GroupByTable = (props: any) => {
  const [updateRecordsModal, toggleUpdateRecordsModal] = useToggle();
  const [mergeDrawer, toggleMergeDrawer, setMergeDrawer] = useToggle();
  const [bulkApplyTags, toggleBulkApplyTags] = useToggle();
  const [createRecordsDrawer, toggleCreateRecordsDrawer] = useToggle();
  const [dTActive, toggleDataTransforms] = useToggle();
  const [clearRecords, setClearRecords] = useState(false); // Triggers update in child tables - Clearing their records
  const [allSelected, setAllSelected] = useState<{
    [key: string]: { [key: string]: number };
  }>({});
  const [selectedPrefill, setSelectedPrefill] = useState<Prefill | null>(null);
  const [exportDrawer, setExportDrawer] = useState(false);
  const [addToListDrawer, setAddToListDrawer] = useState(false);
  const defaultPrefill = props.defaultPrefill;

  const { module: currentModule } = useModule(props?.moduleAPIName);
  const { confirmBulkDelete, bulkDeleteRecords } = useBulkDeleteRecords();
  const { workspaceID, pageID }: any = useParams();
  const { page } = usePage(pageID);

  const { foundLayout } = props;

  const preparedData: Array<GroupData> = useMemo(() => {
    const groupKey = props.group.includes(".")
      ? props.group.split(".")[0]
      : props.group;
    let groups = props?.data?.map((group: any) => {
      const groupMetaData =
        groupKey === "Tags" ? group?.GroupBy?.Name : group?.GroupBy?.[groupKey];

      const keys = Object.keys(group);
      let aggregationData = {};

      for (let key of keys) {
        if (!["Count", "Avg", "Max", "Min", "Sum"].includes(key)) continue;

        aggregationData = { ...aggregationData, [key]: group[key] };
      }

      return {
        data: group?.GroupBy?.records,
        title: GetGroupTitle(groupMetaData, props.group, currentModule),
        id: group?.GroupBy?.[props?.group],
        ...(!!Object.keys(aggregationData)?.length && {
          aggregation: aggregationData,
        }),
      };
    });

    if (!foundLayout.EmptyGroup) {
      groups = groups.filter((group: any) => group.id !== null);
    }

    // Sort groups according to the order of the current layout.
    if (foundLayout?.Order) {
      const orderedGroups = foundLayout.Order.reduce(
        (acc: any, group: string) => {
          const foundGroup = groups.find((g: any) => g.title === group);
          if (foundGroup) acc.push(foundGroup);
          return acc;
        },
        []
      );

      const missingGroups = groups.filter(
        (g: any) => !orderedGroups.includes(g)
      );
      groups = [...orderedGroups, ...missingGroups];
    }

    return groups;
  }, [props.data, props.group, currentModule, foundLayout]);

  // Used when the user adds a Record to a group.
  const groupPrefills: Record<string, { Field: string; Value: string }> =
    useMemo(() => {
      if (!preparedData) return {};

      const isArray =
        currentModule?.Fields.find((m: any) => m.APIName === props.group)
          ?.FieldType === "MultiReference" || false;

      return preparedData.reduce((acc: any, group: GroupData) => {
        acc[group.title] = {
          Field: props.group,
          Value: isArray ? [group.id] : group.id,
        };
        return acc;
      }, {});
    }, [preparedData]);

  const selectedRecords = Object.keys(allSelected).reduce<any[]>((arr, key) => {
    const selected = allSelected[key];
    const records = preparedData?.find((i: any) => i.title === key)?.data ?? [];

    for (const key of Object.keys(selected)) {
      const recordIndex = parseInt(key);

      if (records?.length && records[recordIndex])
        arr.push(records[recordIndex]);
    }

    return arr;
  }, []);

  const onCreateRecordDrawerClose = useCallback(() => {
    toggleCreateRecordsDrawer();
    setSelectedPrefill(null);
  }, []);

  const onCreateRecordDrawerCreate = useCallback(() => {
    toggleCreateRecordsDrawer();
    setSelectedPrefill(null);
    setAllSelected({});
  }, []);

  if (props.loading) {
    return (
      <>
        <div className="mb-3">
          <SkeletonLoader width="300" />
          <SkeletonLoader width="300" height={150} />
        </div>
        <div className="mb-3">
          <SkeletonLoader width="300" />
          <SkeletonLoader width="300" height={150} />
        </div>
      </>
    );
  }

  const selectedRecordIds = selectedRecords.map((r: any) => r._id || r.id);

  return (
    <>
      {Array.isArray(preparedData) && preparedData?.length > 0 ? (
        preparedData.map((element: any, i: number) => (
          <div
            key={`groupTable-${props.group || "null"}-${
              typeof element?.id === "string" ? element?.id : i
            }`}
            className="mb-3"
          >
            <TableInstance
              {...props}
              index={i}
              disabledCreateRecordButton={!!currentModule?.Dynamic}
              key={typeof element?.id === "string" ? element?.id : i}
              tableTitle={element.title}
              data={element.data}
              allSelected={allSelected}
              setAllSelected={setAllSelected}
              selectedRecords={allSelected?.[element?.title] || {}}
              clearRecords={clearRecords}
              setClearRecords={setClearRecords}
              aggregationData={element?.aggregation}
              toggleCreateRecordsDrawer={toggleCreateRecordsDrawer}
              handleCreateRecord={() => {
                setSelectedPrefill({
                  PrefillPairs: [
                    ...(defaultPrefill?.PrefillPairs || []),
                    groupPrefills[element.title],
                  ],
                } as Prefill);
                toggleCreateRecordsDrawer();
              }}
            />
          </div>
        ))
      ) : (
        <AppEmptyState
          title="No Results"
          description="There are no records matching the current filter."
          icon="mdi-archive-search"
        />
      )}
      {selectedRecords.length > 0 && (
        <SharedSelectedRecordsActions
          selectedRecords={selectedRecords}
          onClear={() => {
            setAllSelected({});
            setClearRecords(true);
          }}
          setExportDrawer={setExportDrawer}
          setAddToListDrawer={setAddToListDrawer}
          toggleUpdateRecordsModal={toggleUpdateRecordsModal}
          toggleBulkApplyTags={toggleBulkApplyTags}
          toggleMergeDrawer={toggleMergeDrawer}
          deleteSelectedRecords={deleteSelectedRecords}
          handleDuplicateRecord={handleDuplicateRecord}
          toggleConvertDrawer={toggleDataTransforms}
        />
      )}
      <UpdateRecordsDrawer
        open={updateRecordsModal}
        onClose={toggleUpdateRecordsModal}
        records={selectedRecords}
        moduleAPIName={props?.moduleAPIName}
        view={props.view}
      />
      <BulkApplyTagsDrawer
        open={bulkApplyTags}
        onClose={toggleBulkApplyTags}
        records={selectedRecords}
        moduleAPIName={props?.moduleAPIName}
        view={props.view}
      />
      <CreateRecordDrawer
        open={createRecordsDrawer}
        onClose={onCreateRecordDrawerClose}
        onCreate={onCreateRecordDrawerCreate}
        moduleAPIName={props?.moduleAPIName}
        prefill={
          selectedPrefill
            ? selectedPrefill?.PrefillPairs ?? emptyPrefill
            : emptyPrefill
        }
        onCreateForm={() => {}}
      />
      <MergeRecordsDrawer
        open={mergeDrawer}
        onClose={() => {
          setMergeDrawer(false);
          setAllSelected({});
        }}
        onMerge={() => setAllSelected({})}
        records={selectedRecords}
        module={currentModule}
      />
      <DataExportDrawer
        open={exportDrawer}
        moduleAPIName={props?.moduleAPIName}
        onClose={() => setExportDrawer(false)}
        query={props.query}
        recordIDs={selectedRecordIds}
      />
      <AddToListDrawer
        open={addToListDrawer}
        moduleAPIName={props?.moduleAPIName}
        recordIDs={selectedRecordIds}
        handleClose={() => setAddToListDrawer(false)}
      />
      <BulkDataTransforms
        open={dTActive}
        records={selectedRecords}
        currentModule={currentModule || undefined}
        onClose={() => {
          setAllSelected({});
          toggleDataTransforms();
        }}
      />
    </>
  );

  async function deleteSelectedRecords() {
    const confirmed = await confirmBulkDelete();

    if (!confirmed) return;

    bulkDeleteRecords({
      moduleAPIName: props.moduleAPIName,
      payload: {
        _UserActivity: {
          WorkspaceID: workspaceID,
          Activity: {
            id: StrUtils.uuidv4(),
            Type: "bulk-delete-in-page",
            Page: {
              id: page.id,
              Name: page.Name,
              Logo: page.Logo,
            },
            View: {
              id: props.view?.Name,
              Name: props.view?.Name,
              Type: props.view?.Type,
            },
          },
        },
        Ids: selectedRecords.map((r: any) => r._id || r.id),
      },
      onSuccess: () => {
        setAllSelected({});
      },
    });
  }

  function handleDuplicateRecord() {
    const record = selectedRecords[0];

    const prefillPairs = Object.keys(record).reduce((arr: any, key) => {
      if (!record[key] && typeof record[key] !== "boolean") {
        return arr;
      }

      arr.push({ Field: key, Value: record[key] });
      return arr;
    }, []);

    //@ts-ignore
    setSelectedPrefill({ PrefillPairs: prefillPairs });
    toggleCreateRecordsDrawer();
  }
};

const TableInstance = (props: any) => {
  const [open, setOpened] = useState(true);
  const [selected, setSelected] = useState({});
  // Override draggingID for GroupByTable, so each group can be dragged independently.
  const [groupDraggingID, setGroupDraggingID] = useState("");

  // Update parent state with selected records
  useEffect(() => {
    props.setAllSelected((prev: any) => ({
      ...prev,
      [props.tableTitle]: selected,
    }));
  }, [selected]);

  // Handle when records were cleared from Toolbar in parent.
  useEffect(() => {
    if (!props.clearRecords) return;

    setSelected({});
    props.setClearRecords(false);
  }, [props.clearRecords]);

  return (
    <>
      <div className="d-flex align-items-center">
        <div
          className="d-flex align-items-center cursor-pointer mr-2"
          onClick={() => setOpened((prev) => !prev)}
        >
          <i
            className={`text-dark mdi p-2 ${
              !open ? "mdi-chevron-right" : "mdi-chevron-down"
            }`}
            style={{ fontSize: "24px" }}
          />
          <h4 className="m-0 text-dark">{props.tableTitle}</h4>
        </div>

        <IconButton
          style={{ position: "relative", top: -1.5 }}
          icon="plus"
          color="primary"
          size="xs"
          onClick={props.handleCreateRecord}
          disabled={props.disabledCreateRecordButton}
        />
      </div>
      <Collapse isOpen={open}>
        <AppTable
          {...props}
          data={props.data}
          selectedRecords={selected}
          setSelectedRecords={setSelected}
          useAggregations={
            props?.viewMode === "Table" ? false : props.useAggregations
          }
          aggregationData={props?.aggregationData}
          draggingID={groupDraggingID}
          setDraggingID={setGroupDraggingID}
        />
      </Collapse>
    </>
  );
};

export default GroupByTable;
